import { pluralizer } from '@utils/pluralizer';
import { type AdminRestaurant } from '../../team/apiHelpers';
import {
  type RestaurantSettingsCancellationPayload,
  type SaveRestaurantSettingsPayload,
} from '../apiHelpers';
import { type GeneralSettingsForm } from './generalSettingsFormTypes';

const MAX_SCHEDULE_DAYS = 365;

export const SCHEDULE_OPTIONS = Array.from(
  { length: MAX_SCHEDULE_DAYS },
  (_, i) => {
    const value = i + 1;
    return { label: `${pluralizer('day', value)} in advance`, value };
  },
);

export const AVAILABILITY_INTERVAL_OPTIONS = [15, 30, 45, 60].map((n) => ({
  label: `${n} minutes`,
  value: n,
}));

export const YES_NO_SELECT_OPTIONS = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 0 },
];

export const SHOW_HIDE_SELECT_OPTIONS = [
  { label: 'Show', value: 1 },
  { label: 'Hide', value: 0 },
];

const phoneForForm = (phone: string): string => {
  if (!phone) {
    return '';
  }

  if (phone.startsWith('+')) {
    return phone;
  }
  if (/^\d{10}$/.test(phone)) {
    return `+1${phone}`;
  }
  return phone;
};

export const restaurantToFormValues = (
  restaurant: AdminRestaurant,
  heroImageKeys: string[] = [],
): GeneralSettingsForm => ({
  name: restaurant.name,
  description: restaurant.description ?? '',
  showGoogleRating: restaurant.showGoogleRating ? 1 : 0,
  processingFeePayer: restaurant.processingFeePayer ?? 'guest',
  dining: restaurant.dining ?? '',
  dressCode: restaurant.dressCode ?? '',
  dressCodeDescription: restaurant.dressCodeDescription ?? '',
  dressCodeRequired: restaurant.dressCodeRequired ? 1 : 0,
  heroImageKeys,
  website: restaurant.website,
  phone: phoneForForm(restaurant.phone),
  supportEmail: restaurant.supportEmail ?? '',
  street: restaurant.street,
  city: restaurant.city,
  state: restaurant.state,
  postalCode: restaurant.postalCode,
  schedule: restaurant.schedule,
  availabilityInterval: restaurant.availabilityInterval,
  optimizeAvailabilities: restaurant.optimizeAvailabilities ? 1 : 0,
  maxGuestCount: restaurant.maxReservationGuests,
  cancellationPolicyEnabled: restaurant.cancellationPolicy ? 1 : 0,
  cancellationFee: restaurant.cancellationPolicy
    ? restaurant.cancellationPolicy.fee / 100
    : 1,
  cancellationHoursBeforeReservation:
    restaurant.cancellationPolicy?.hoursBeforeReservation ?? 24,
  cancellationThreshold: restaurant.cancellationPolicy
    ? String(restaurant.cancellationPolicy.threshold / 100)
    : '',
  coverLimit:
    restaurant.coverLimit === null || restaurant.coverLimit === undefined
      ? ''
      : String(restaurant.coverLimit),
  pacingEnabled: restaurant.pacingControls ? 1 : 0,
  pacingLimit: restaurant.pacingControls?.pacingLimit ?? 1,
  pacingWindow: restaurant.pacingControls?.pacingWindow ?? 15,
});

export const buildCancellationPolicyPayloadForSave = (
  data: GeneralSettingsForm,
  snapshot: AdminRestaurant,
): RestaurantSettingsCancellationPayload => {
  if (data.cancellationPolicyEnabled === 0) {
    return { action: 'unchanged' };
  }
  const feeCents = Math.round(Number(data.cancellationFee) * 100);
  const thresholdDollars = Number.parseInt(
    data.cancellationThreshold.trim(),
    10,
  );
  const thresholdCents = thresholdDollars * 100;
  const hours = data.cancellationHoursBeforeReservation;
  const policy = snapshot.cancellationPolicy;

  if (!policy) {
    return {
      action: 'create',
      fee: feeCents,
      hoursBeforeReservation: hours,
      threshold: thresholdCents,
    };
  }
  if (
    policy.fee === feeCents &&
    policy.hoursBeforeReservation === hours &&
    policy.threshold === thresholdCents
  ) {
    return { action: 'unchanged' };
  }
  return {
    action: 'update',
    cancellationPolicyId: policy.id,
    fee: feeCents,
    hoursBeforeReservation: hours,
    threshold: thresholdCents,
  };
};

export const buildRestaurantSettingsPayload = (
  data: GeneralSettingsForm,
  cancellationPolicy: RestaurantSettingsCancellationPayload,
): SaveRestaurantSettingsPayload => {
  const raw = data.coverLimit.trim();
  const coverParsed = raw === '' ? null : Number(raw);
  const coverLimit =
    coverParsed === null || Number.isNaN(coverParsed) ? null : coverParsed;

  return {
    supportEmail:
      data.supportEmail.trim() === '' ? null : data.supportEmail.trim(),
    description: data.description.trim(),
    showGoogleRating: data.showGoogleRating === 1,
    processingFeePayer: data.processingFeePayer,
    dining: data.dining.trim() === '' ? null : data.dining.trim(),
    dressCode: data.dressCode.trim() === '' ? null : data.dressCode.trim(),
    dressCodeDescription:
      data.dressCodeDescription.trim() === ''
        ? null
        : data.dressCodeDescription.trim(),
    dressCodeRequired:
      data.dressCode.trim() !== '' && data.dressCodeRequired === 1,
    schedule: data.schedule,
    availabilityInterval: data.availabilityInterval,
    optimizeAvailabilities: data.optimizeAvailabilities === 1,
    maxGuestCount: data.maxGuestCount,
    coverLimit,
    pacingEnabled: data.pacingEnabled === 1,
    pacingLimit: data.pacingLimit,
    pacingWindow: data.pacingWindow,
    cancellationPolicy,
    heroImageKeys: data.heroImageKeys ?? [],
  };
};
